<!-- WaivedBillModal.vue -->
<template>
  <div class="modal fade" id="waivedBillModal" tabindex="-1" role="dialog">
    <div class="modal-dialog modal-lg modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header bg-warning text-dark">
          <h5 class="modal-title">
            <i class="mdi mdi-file-cancel-outline me-2"></i>
            Waived Bill Details
          </h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
        </div>

        <div class="modal-body">
          <!-- Waiver Status Banner -->
          <div class="alert alert-warning border-0 mb-4" role="alert">
            <div class="d-flex align-items-center">
              <div class="flex-shrink-0">
                <i class="mdi mdi-file-cancel-outline h2 mb-0"></i>
              </div>
              <div class="flex-grow-1 ms-3">
                <h6 class="alert-heading mb-1">Bill Amount Waived</h6>
                <p class="mb-0">This bill has been granted a waiver and the amount has been forgiven.</p>
              </div>
            </div>
          </div>

          <div class="row">
            <div class="col-6">
              <address>
                <strong>Bill Prepared for:</strong><br>
                {{ billDetail.customerPhoneNumber }}<br>
                {{ billDetail.description }}<br><br>
                <strong>Date Created:</strong> {{ billDetail.dateCreated }}
              </address>
            </div>
            <div class="col-6 text-end">
              <div class="badge bg-warning text-dark fs-6 px-3 py-2 mb-3">
                <i class="mdi mdi-file-cancel-outline me-1"></i>
                WAIVED
              </div>
            </div>
          </div>

          <div class="col-12">
            <div class="py-2 mt-3">
              <h4 class="fw-bold text-warning">{{ billNo }}</h4>
            </div>
          </div>

          <div class="col-12">
            <div class="table-responsive">
              <table class="table table-nowrap">
                <thead class="bg-light">
                <tr>
                  <th style="width: 70px;">No.</th>
                  <th>Customer</th>
                  <th>Description</th>
                  <th class="text-end">Original Amount</th>
                  <th class="text-center">Status</th>
                </tr>
                </thead>

                <tbody>
                <tr v-for="(item, index) in billInfo" :key="index">
                  <td>{{ index + 1 }}</td>
                  <td>
                    <div class="d-flex align-items-center">
                      <div class="flex-grow-1">
                        <h6 class="mb-1">{{ item.customer }}</h6>
                        <small class="text-muted">Customer</small>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div>
                      <span class="fw-medium">{{ item.feeDescription }}</span>
                    </div>
                  </td>
                  <td class="text-end">
                    <div class="text-decoration-line-through text-muted">
                      KES {{ formatPrice(item.billTotal) }}
                    </div>
                    <small class="text-warning fw-bold">WAIVED</small>
                  </td>
                  <td class="text-center">
                      <span class="badge bg-warning text-dark">
                        <i class="mdi mdi-file-cancel-outline me-1"></i>Waived
                      </span>
                  </td>
                </tr>
                </tbody>

                <tfoot class="bg-light">
                <tr>
                  <td colspan="3" class="border-0 text-end">
                    <strong class="h5">Original Total:</strong>
                  </td>
                  <td class="border-0 text-end">
                    <div class="text-decoration-line-through text-muted h5 mb-1">
                      KES {{ formatPrice(billDetail.detailAmount) }}
                    </div>
                    <h4 class="m-0 fw-bold text-warning">
                      WAIVED
                    </h4>
                  </td>
                  <td class="border-0 text-center">
                      <span class="badge bg-warning text-dark fs-6">
                        <i class="mdi mdi-file-cancel-outline me-1"></i>FORGIVEN
                      </span>
                  </td>
                </tr>
                </tfoot>
              </table>
            </div>
          </div>

          <!-- Waiver Summary -->
          <div class="row mt-4">
            <div class="col-12">
              <div class="card border-warning">
                <div class="card-header bg-light">
                  <h6 class="card-title mb-0">
                    <i class="mdi mdi-file-document-outline me-2"></i>
                    Waiver Summary
                  </h6>
                </div>
                <div class="card-body">
                  <div class="row">
                    <div class="col-3">
                      <div class="text-center">
                        <h6 class="text-muted mb-1">Original Amount</h6>
                        <h5 class="mb-0 text-decoration-line-through text-muted">
                          {{ formatPrice(billDetail.detailAmount) }}
                        </h5>
                      </div>
                    </div>
                    <div class="col-3">
                      <div class="text-center">
                        <h6 class="text-muted mb-1">Waived Amount</h6>
                        <h5 class="mb-0 text-warning">{{ formatPrice(billDetail.detailAmount) }}</h5>
                      </div>
                    </div>
                    <div class="col-3">
                      <div class="text-center">
                        <h6 class="text-muted mb-1">Amount Due</h6>
                        <h5 class="mb-0 text-success">KES 0.00</h5>
                      </div>
                    </div>
                    <div class="col-3">
                      <div class="text-center">
                        <h6 class="text-muted mb-1">Status</h6>
                        <span class="badge bg-warning text-dark">WAIVED</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Waiver Information -->
          <div class="row mt-3">
            <div class="col-12">
              <div class="alert alert-light border border-warning" role="alert">
                <h6 class="alert-heading">
                  <i class="mdi mdi-information-outline me-2"></i>
                  Waiver Information
                </h6>
                <p class="mb-2">
                  <strong>Waiver Type:</strong> Bill Amount Waiver<br>
                  <strong>Processed By:</strong> {{ billDetail.createdBy || 'System Admin' }}<br>
                  <strong>Date Processed:</strong> {{ billDetail.dateCreated }}
                </p>
                <hr>
                <p class="mb-0">
                  <small class="text-muted">
                    <i class="mdi mdi-shield-check-outline me-1"></i>
                    This waiver has been approved and processed according to county policies.
                    The original bill amount has been forgiven and no payment is required.
                  </small>
                </p>
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer bg-light">
          <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">
            <i class="mdi mdi-close me-1"></i>Close
          </button>

          <button v-if="getRight('REVERT WAIVER')"
                  @click="revertWaiver"
                  class="btn btn-outline-danger">
            <i class="mdi mdi-backup-restore me-1"></i>Revert Waiver
          </button>

          <button @click="printBill" class="btn btn-warning">
            <i class="mdi mdi-printer me-1"></i>Print Bill
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import { getRight } from "@/api";

export default {
  name: "WaivedBillModal",
  props: {
    billDetail: {
      type: Object,
      default: () => ({})
    },
    billInfo: {
      type: Array,
      default: () => []
    },
    billNo: {
      type: String,
      default: ''
    }
  },
  methods: {
    getRight,

    formatPrice(value) {
      if (!value && value !== 0) return '0.00';
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'KES',
        minimumFractionDigits: 2
      }).format(value).replace('KES', '').trim();
    },

    printBill() {
      this.$emit('print-bill');
    },

    revertWaiver() {
      if (confirm('Are you sure you want to revert this waiver? This action cannot be undone.')) {
        this.$emit('revert-waiver', this.billDetail.id);

        // Close the modal after confirming revert
        const modalElement = document.getElementById('waivedBillModal');
        if (modalElement) {
          const bootstrapModal = bootstrap.Modal.getInstance(modalElement);
          if (bootstrapModal) {
            bootstrapModal.hide();
          }
        }
      }
    }
  }
}
</script>

<style scoped>
.modal-header.bg-warning {
  border-bottom: none;
}

.modal-footer.bg-light {
  border-top: 1px solid #dee2e6;
}

.table th {
  font-weight: 600;
  color: #495057;
  background-color: #f8f9fa !important;
}

.badge {
  font-size: 0.75rem;
}

.badge.fs-6 {
  font-size: 0.875rem !important;
}

.card-title {
  color: #495057;
  font-weight: 600;
}

.text-warning {
  color: #ffc107 !important;
}

.bg-warning {
  background-color: #ffc107 !important;
}

.border-warning {
  border-color: #ffc107 !important;
}

.text-decoration-line-through {
  text-decoration: line-through !important;
}

.alert-warning {
  background-color: rgba(255, 193, 7, 0.1);
  border-color: rgba(255, 193, 7, 0.2);
  color: #856404;
}

.h2 {
  font-size: 2rem;
}

.alert-light {
  background-color: #f8f9fa;
  color: #6c757d;
}
</style>